/**
 * Responsible for rendering a product specific zoom
 * @module modules/productZoom
 */
import $ from 'jquery';
import cookies from 'js-cookie';
import './slick-carousel';
import Keyboard from './keyboard';

import toggleFavorite from '../elements/toggle-favorite';
import registerJQueryPlugin from '../lib/register-jquery-plugin';
import sliderZoom from './zoom/slider-zoom';
import dataProps from '../lib/data-props';

/**
 * Defines the options which can be provided for the product zoom.
 * @type {Object}
 */
const DEFAULTS = {
  sliderZoom,
  targetSelector: '',
  slideFunc: num => num,
  didUnmount: () => {},
  zoomBadgeDuration: 2000,
  returnFocus: '',
  trapFocusFunc: Keyboard.trapFocus.bind(Keyboard),
};

/**
 * Defines the default props for the component.
 * @type {Object}
 */
const DEFAULT_PROPS = {
  images: [],
  imagesRetina: [],
  noImages: 'No images found :(',
  tapToZoom: 'Tap to Zoom',
  pinchToZoom: 'Pinch to Zoom',
};

/**
 * Defines the id for the favorite element.
 * @type {String}
 */
export const FAVORITE_ID = 'product-zoom-favorite';

/**
 * Defines the fade out className.
 * @type {String}
 */
export const FADE_OUT_BADGE_CLASS_NAME = 'product-badge--fade-out';

/**
 * Defines the element ID for a zoom badge.
 * @type {String}
 */
export const ZOOM_BADGE_ID = 'zoom-badge';

/**
 * Defines the event used for finishing a transition.
 * @type {String}
 */
export const ZOOM_BADGE_TRANSITION_EVENT = 'transitionend';

/**
 * Contains fields for interacting with a badge cookie.
 * @type {Object}
 */
export const ZOOM_BADGE_COOKIE = {
  name: 'zb',
  expires: 14,
};

/**
 * Contains fields for interacting with a pinch to zoom cookie.
 * @type {Object}
 */
export const PINCH_ZOOM_BADGE_COOKIE = {
  name: 'pzb',
  expires: 14,
};

/**
 * Setup used for hiding the zoom badge so that it can be shown and later on
 * removed.
 * @private
 * @param  {number} zoomBadgeDuration - The duration in milliseconds when the
 *                                      badge should be removed
 */
const setupHideZoomBadge = (zoomBadgeDuration) => {
  setTimeout(() => {
    const $zoomBadge = $(document.getElementById(ZOOM_BADGE_ID));
    $zoomBadge
      .addClass(FADE_OUT_BADGE_CLASS_NAME)
      .remove();
  }, zoomBadgeDuration);
};

/**
 * Contains the component used for rendering zoom info badge.
 * @private
 * @param  {string} text - Text used in the badge
 * @return {string} Rendered template containing the badge
 */
const infoBadgeComponent = ({ text }) => `
  <div id="${ZOOM_BADGE_ID}" class="product-badge product-badge--center u-absolute product-badge--inline" tabindex="0">
    ${text}
  </div>`;

/**
 * Renders an info badge if the necessary cookie isn't present.
 * @private
 * @param  {number} zoomBadgeDuration - Represents duration in milliseconds
 * @param  {string} text - Text used inside zoom badge
 * @return {string} The info badge if the logic checks out
 */
const renderInfoBadge = ({ zoomBadgeDuration, text, badgeCookie }) => {
  const { name, expires } = badgeCookie;
  const sawZoomBadge = cookies.get(name);

  if (!sawZoomBadge) {
    cookies.set(name, 1, { expires });
    setupHideZoomBadge(zoomBadgeDuration);
    return infoBadgeComponent({ text });
  }

  return '';
};

/**
 * Renders any additional markup that goes in the menu.
 * @private
 * @return {string} A fully render template for the menu.
 */
const favoriteButtonComponent = () => {
  const $favBtn = $('.js-fav-button');
  let extraCss = '';
  if ($favBtn.hasClass('favorite-button--active')) {
    extraCss = 'favorite-button--active';
  }
  if ($favBtn.hasClass('favorite-button--men')) {
    extraCss += ' favorite-button--men';
  }

  const code = $favBtn.data('code');
  const favMsg = $favBtn.data('favorite-message');
  const unFavMsg = $favBtn.data('unfavorite-message');
  const notification = $favBtn.data('favorite-notification');
  const unFavNotification = $favBtn.data('unfavorite-notification');
  const markup = `<button id="${FAVORITE_ID}" class="favorite-button zoom-menu__favorite ${extraCss}"
      data-code="${code}"
      data-favorite-message="${favMsg}"
      data-unfavorite-message="${unFavMsg}"
      data-favorite-notification="${notification}"
      data-unfavorite-notification="${unFavNotification}"
    >
    <span class="favorite-button__icon icon icon--heart-fill">
      <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <path d="M7.093 2.228a4.129 4.129 0 0 0-5.876 0 4.218 4.218 0 0 0 0 5.928L8 15l6.783-6.844a4.219 4.219 0 0 0 0-5.928 4.129 4.129 0 0 0-5.876 0L8 3.143z"/>
      </svg>
    </span>
  </button>`;

  return markup;
};


const logFavoriteClick = (isAddedToFavorites) => {
  if (typeof window.logEngagementWithDetails === 'function') {
    window.logEngagementWithDetails(window.location.href, 'pdp zoom modal', 'add to favorites',
        isAddedToFavorites ? 'added' : 'removed');
  }
};

/**
 * Used to bind the events for the product zoom.
 * @private
 */
const didRender = () => {
  const btn = document.getElementById(FAVORITE_ID);
  toggleFavorite(btn, {
    logFavoriteClick,
  });
  if (typeof window.addFavEvent === 'function') {
    window.addFavEvent(btn);
  }
};

/**
 * Determines whether a function should be ran based on images existing.
 * @private
 * @param  {array}   images - A list of image sources
 * @param  {Function} callback - Callback to return if logic is valid
 * @return {Function} Used for execution based on logic
 */
const shouldRun = (images, callback) => (images.length ? callback : () => '');

/**
 * Responsible for adding the click zoom event listener
 * @param {HTMLElement} el - The element used for opening up the product zoom
 * @param {Object} [options={}] - Forwarded along options
 */
function addZoomClickListener(el, opts) {
  const {
    sliderZoom: sliderZoomFunc,
    zoomBadgeDuration,
    targetSelector,
  } = opts;
  const { images, imagesRetina, noImages, pinchToZoom } = dataProps(el, DEFAULT_PROPS);

  $(el).on('click.product-zoom', targetSelector, (e) => {
    /* Mock Content Start UX-20630 */
    // AB-test guard: when the click originates inside a mobile video trigger
    // (e.g. the inline employee-video tile at gallery slide 3), the trigger
    // opens its own fullscreen overlay via mobileVideoPlayer — the product
    // zoom modal should not also open behind it.
    if ($(e.target).closest('.js-mobile-video-trigger').length) {
      return;
    }
    /* Mock Content End UX-20630 */

    const $slickActive = $(e.target).closest('.slick-active');
    const slickIndex = $slickActive.data('slick-index');

    if (slickIndex === undefined || slickIndex < 0) {
      return; // Exit early for invalid or cloned slides
    }
    handleZoom(
      e,
      el,
      opts,
      sliderZoomFunc,
      zoomBadgeDuration,
      noImages,
      pinchToZoom,
      images,
      imagesRetina,
      slickIndex,
    );
  });
  $(el).on('keydown.product-zoom', (e) => {
    const $slickActive = $(e.target).find('.slick-active');
    const slickIndex = $slickActive.data('slick-index');
    if (e.key === 'Enter' || e.keyCode === 13) {
      if ($(e.target).closest('.slick-arrow').length > 0) {
        return;
      }
      handleZoom(
        e,
        el,
        opts,
        sliderZoomFunc,
        zoomBadgeDuration,
        noImages,
        pinchToZoom,
        images,
        imagesRetina,
        slickIndex,
      );
    }
  });
}

const logOpenZoomModal = () => {
  if (typeof window.logEngagement === 'function') {
    window.logEngagement(window.location.href, 'pdp zoom modal', 'open zoom');
  }
};

function handleZoom(
  e,
  el,
  opts,
  sliderZoomFunc,
  zoomBadgeDuration,
  noImages,
  pinchToZoom,
  images,
  imagesRetina,
  slickIndex,
) {
  // stylitics
  const $imageLink = $(e.target).parent('a');
  if ($imageLink.data('styliticsShop')) {
    return;
  }

  const startingIndex = images.length ? slickIndex: null;

  sliderZoomFunc(images, $.extend({}, opts, {
    images,
    imagesRetina,
    menuRender: shouldRun(images, favoriteButtonComponent),
    didRender: shouldRun(images, didRender),
    startingIndex,
    noImages,
    zoomClickListener: () => {
      addZoomClickListener(el, opts);
    },
    imageLoaded: ($dialog) => {
      const badgeHTML = renderInfoBadge({
        text: pinchToZoom,
        zoomBadgeDuration,
        badgeCookie: PINCH_ZOOM_BADGE_COOKIE,
      });

      $dialog.prepend(badgeHTML);
    },
  }));

  logOpenZoomModal();
}

/**
 * Responsible for rendering a slider zoom that's specifically used showcasing
 * a product.
 * @see DEFAULTS
 * @param {HTMLElement} el - The element used for opening up the product zoom
 * @param {Object} [options={}] - Forwarded along options
 */
export default function productZoom(el, options = {}) {
  const opts = $.extend({}, DEFAULTS, options);
  const { zoomBadgeDuration } = opts;
  const { images, tapToZoom } = dataProps(el, DEFAULT_PROPS);

  $(el)
    .append(shouldRun(
      images,
      renderInfoBadge({ text: tapToZoom, zoomBadgeDuration, badgeCookie: ZOOM_BADGE_COOKIE }),
    ));
  addZoomClickListener(el, opts);
}

registerJQueryPlugin('productZoom', productZoom);



// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/product-zoom.js